import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  type MutableRefObject,
} from 'react';
import { FeatureGroup, useMap } from 'react-leaflet';
import { EditControl as EditControlRaw } from 'react-leaflet-draw';
import L from './leafletDrawAreaFix';
import 'leaflet-draw/dist/leaflet.draw.css';

// react-leaflet-draw's typings are stale; the underlying control is just a
// regular Leaflet Control wrapped in a tiny react component. Cast once
// here so the rest of the file stays strictly typed.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const EditControl = EditControlRaw as any;

/**
 * Default polygon draw config. Matches the legacy `MapComponent` defaults so
 * migrating callers don't visually change. Frozen so the object identity is
 * stable across renders (react-leaflet-draw re-mounts the control whenever
 * its `draw` / `edit` prop identity changes, which cancels in-progress draws).
 */
export const DEFAULT_DRAW_OPTIONS = Object.freeze({
  rectangle: false,
  circle: false,
  circlemarker: false,
  marker: { repeatMode: false },
  polyline: false,
  polygon: {
    allowIntersection: false,
    showArea: true,
    drawError: {
      color: '#e1e100',
      message: '<strong>Error:</strong> Shape edges cannot cross!',
    },
    shapeOptions: {
      color: '#e64a19',
      fillColor: '#ff7d47',
      fillOpacity: 0.4,
      weight: 2,
    },
  },
});

/**
 * Default edit config. `edit` / `remove` must be either `false` or an
 * options object — leaflet-draw crashes when fed booleans because it tries
 * to call `L.Util.extend(options.edit, defaults)` and explodes on
 * `true.selectedPathOptions = …`.
 */
export const DEFAULT_EDIT_OPTIONS = Object.freeze({
  poly: { allowIntersection: false },
  edit: {},
  remove: {},
});

export interface DrawnEvent {
  /** The wrapped leaflet-draw event, in case the caller needs raw access. */
  raw: L.LeafletEvent;
  /** `[lat, lng]` points — the first ring for a polygon, the full vertex
   *  chain for a polyline — or null when the event was for a shape neither
   *  extractor supports. */
  positions: Array<[number, number]> | null;
  /** Which extractor produced `positions`/`geometry`, so a caller enabling
   *  more than one tool can tell them apart. Undefined for marker events. */
  shapeType?: 'polygon' | 'polyline';
  /** Area in hectares, computed from `L.GeometryUtil.geodesicArea`.
   *  Always null for a polyline — a line has no area. */
  areaHectares: number | null;
  /** Convenience: the layer's GeoJSON geometry. Saves callers a
   *  `layer.toGeoJSON()` dance, especially in the vertex-drag relay
   *  where the synthetic event doesn't carry the layer. */
  geometry: GeoJSON.Polygon | GeoJSON.LineString | null;
  /** `[lat, lng]` of a dropped marker, when the event was for the marker
   *  (drop-pin) tool rather than a polygon. `null` for polygon events. */
  marker?: [number, number] | null;
}

export interface MapDrawControlHandle {
  /** Imperatively clear every polygon currently in the FeatureGroup. */
  clearPolygons: () => void;
  /** Access to the underlying FeatureGroup (e.g. for advanced seeding). */
  getFeatureGroup: () => L.FeatureGroup | null;
}

interface MapDrawControlProps {
  /** Toolbar position. Defaults to top-right to match legacy MapComponent. */
  position?: 'topleft' | 'topright' | 'bottomleft' | 'bottomright';
  /**
   * Initial geometry to seed into the FeatureGroup as an editable layer.
   * Accepts a GeoJSON Polygon or MultiPolygon (only the first ring of the
   * first polygon is seeded — multi-ring editing is out of scope). Pass
   * `null` for the fresh-draw case.
   *
   * Reseeds whenever this prop's identity changes — keep it stable (memo or
   * once-on-mount) to avoid clobbering an in-flight edit.
   */
  initialGeometry?: GeoJSON.Polygon | GeoJSON.MultiPolygon | null;
  /** Leaflet-draw `draw` options. MUST be a stable reference (see above). */
  drawOptions?: object;
  /** Leaflet-draw `edit` options. MUST be a stable reference. */
  editOptions?: object;
  /**
   * Forwarded to the caller — both on `draw:created` and on each subsequent
   * vertex-drag `edit` event of the resulting layer. leaflet-draw only fires
   * `draw:edited` on toolbar Save, which is easy to miss, so we relay every
   * vertex-drag as an edit too.
   */
  onCreated?: (e: DrawnEvent) => void;
  onEdited?: (e: DrawnEvent) => void;
  onDeleted?: (e: { raw: L.LeafletEvent; allCleared: boolean }) => void;
  /** Forwarded `true` while the user is in the middle of drawing/editing,
   *  `false` once they stop. Lets the page pause auto-recenter, etc. */
  onDrawingStateChange?: (drawing: boolean) => void;
  /** Escape hatch — receive the underlying FeatureGroup ref so callers can
   *  seed it imperatively (the wizard's pre-existing pattern). */
  featureGroupRef?: MutableRefObject<L.FeatureGroup | null>;
}

interface InnerFG {
  // react-leaflet attaches the Leaflet layer object directly on the ref.
  _layers?: Record<string, L.Layer>;
  removeLayer: (layer: L.Layer) => InnerFG;
  addLayer: (layer: L.Layer) => InnerFG;
}

function polygonToHectares(latLngs: L.LatLng[] | null | undefined): number | null {
  if (!latLngs || !Array.isArray(latLngs) || latLngs.length < 3) return null;
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const area = (L as any).GeometryUtil?.geodesicArea?.(latLngs);
    if (typeof area !== 'number' || Number.isNaN(area)) return null;
    return Number((area / 10000).toFixed(2));
  } catch (err) {
    console.error('MapDrawControl: failed to compute area', err);
    return null;
  }
}

function firstRingPositions(layer: L.Layer): Array<[number, number]> | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const anyLayer = layer as any;
  if (typeof anyLayer.getLatLngs !== 'function') return null;
  const ring = anyLayer.getLatLngs()?.[0];
  if (!Array.isArray(ring)) return null;
  return ring
    .filter((p: L.LatLng) => typeof p?.lat === 'number' && typeof p?.lng === 'number')
    .map((p: L.LatLng) => [p.lat, p.lng] as [number, number]);
}

// A polyline's getLatLngs() is a FLAT array (unlike a polygon's, which is
// nested one ring deep) — firstRingPositions would read its first *point*
// as if it were the whole ring and bail. Separate extractor, same shape out.
function linePositions(layer: L.Layer): Array<[number, number]> | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const anyLayer = layer as any;
  if (typeof anyLayer.getLatLngs !== 'function') return null;
  const points = anyLayer.getLatLngs();
  if (!Array.isArray(points)) return null;
  return points
    .filter((p: L.LatLng) => typeof p?.lat === 'number' && typeof p?.lng === 'number')
    .map((p: L.LatLng) => [p.lat, p.lng] as [number, number]);
}

function layerToGeoJSON(layer: L.Layer): GeoJSON.Polygon | GeoJSON.LineString | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const anyLayer = layer as any;
  if (typeof anyLayer.toGeoJSON !== 'function') return null;
  try {
    const f = anyLayer.toGeoJSON() as GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.LineString>;
    return f?.geometry ?? null;
  } catch {
    return null;
  }
}

/**
 * `react-leaflet-draw` `EditControl` wrapped in a `FeatureGroup`, with the
 * `L.GeometryUtil` area-calc patch guaranteed to have run (via the
 * `leafletDrawAreaFix` import) and with the wizard's vertex-drag relay
 * already baked in.
 *
 * Compose this inside a `MapContainer`. The unified `MapCanvas` mounts it
 * conditionally based on its `drawing` prop.
 */
export const MapDrawControl = forwardRef<MapDrawControlHandle, MapDrawControlProps>(
  function MapDrawControl(
    {
      position = 'topright',
      initialGeometry,
      drawOptions = DEFAULT_DRAW_OPTIONS,
      editOptions = DEFAULT_EDIT_OPTIONS,
      onCreated,
      onEdited,
      onDeleted,
      onDrawingStateChange,
      featureGroupRef: externalFGRef,
    },
    handleRef,
  ) {
    const map = useMap();
    const internalFGRef = useRef<L.FeatureGroup | null>(null);
    const editableLayerRef = useRef<L.Layer | null>(null);

    // Mirror the FG ref out to the caller. The external ref is a useRef from
    // the parent — we just keep it pointed at the same object.
    const setFGRef = useCallback(
      (fg: L.FeatureGroup | null) => {
        internalFGRef.current = fg;
        if (externalFGRef) externalFGRef.current = fg;
      },
      [externalFGRef],
    );

    useImperativeHandle(
      handleRef,
      () => ({
        getFeatureGroup: () => internalFGRef.current,
        clearPolygons: () => {
          const fg = internalFGRef.current as unknown as InnerFG | null;
          if (!fg || !fg._layers) return;
          Object.values(fg._layers).forEach((layer) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if ((L as any).Polygon && layer instanceof (L as any).Polygon) {
              fg.removeLayer(layer);
            }
          });
        },
      }),
      [],
    );

    // Forward drawing-lifecycle events from the underlying map so the parent
    // can pause auto-recenter / hide UI while a draw is in flight.
    useEffect(() => {
      if (!map || !onDrawingStateChange) return undefined;
      const onStart = () => onDrawingStateChange(true);
      const onStop = () => onDrawingStateChange(false);
      const events: Array<[string, () => void]> = [
        ['draw:drawstart', onStart],
        ['draw:drawstop', onStop],
        ['draw:editstart', onStart],
        ['draw:editstop', onStop],
        ['draw:deletestart', onStart],
        ['draw:deletestop', onStop],
      ];
      events.forEach(([name, handler]) => map.on(name, handler));
      return () => {
        events.forEach(([name, handler]) => map.off(name, handler));
      };
    }, [map, onDrawingStateChange]);

    // Seed an existing GeoJSON polygon as an editable layer. Distinct from
    // a freshly drawn polygon: leaflet-draw needs the layer inside the
    // FeatureGroup at edit-toolbar-click time, so we add it on mount.
    useEffect(() => {
      const fg = internalFGRef.current;
      if (!fg || !initialGeometry || typeof initialGeometry !== 'object') return undefined;

      let ring: number[][] | null = null;
      if (initialGeometry.type === 'Polygon') {
        ring = (initialGeometry.coordinates?.[0] as number[][]) ?? null;
      } else if (initialGeometry.type === 'MultiPolygon') {
        ring = (initialGeometry.coordinates?.[0]?.[0] as number[][]) ?? null;
      }
      if (!ring || ring.length < 3) return undefined;

      const latLngs = ring.map(([lng, lat]) => [lat, lng] as [number, number]);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const layer = (L as any).polygon(latLngs, {
        color: '#e64a19',
        fillColor: '#ff7d47',
        fillOpacity: 0.35,
        weight: 2,
      });
      try {
        fg.addLayer(layer);
        editableLayerRef.current = layer;

        if (onEdited) {
          layer.on('edit', () => {
            const positions = firstRingPositions(layer);
            const area = polygonToHectares(layer.getLatLngs()?.[0] as L.LatLng[]);
            onEdited({
              raw: { type: 'edit' } as L.LeafletEvent,
              positions,
              areaHectares: area,
              geometry: layerToGeoJSON(layer),
            });
          });
        }
      } catch (err) {
        console.error('MapDrawControl: failed to seed initial geometry', err);
      }

      return () => {
        if (layer && fg) {
          try {
            fg.removeLayer(layer);
          } catch {
            /* ignore */
          }
        }
        if (editableLayerRef.current === layer) {
          editableLayerRef.current = null;
        }
      };
    }, [initialGeometry, onEdited]);

    // Stable event handlers — passing inline lambdas to EditControl re-mounts
    // the control on every render and cancels in-flight draws.
    const handleCreated = useCallback(
      (e: L.LeafletEvent) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { layerType, layer } = e as any;

        // Marker (drop-pin) tool: surface the lat/lng to the caller and drop
        // the transient marker layer that EditControl just added — the page
        // keeps its own managed pin, so we don't want a duplicate in the FG.
        if (layerType === 'marker' && layer && typeof layer.getLatLng === 'function') {
          const ll = layer.getLatLng();
          const fg = internalFGRef.current as unknown as InnerFG | null;
          try {
            fg?.removeLayer(layer as L.Layer);
          } catch {
            /* ignore */
          }
          onCreated?.({
            raw: e,
            positions: null,
            areaHectares: null,
            geometry: null,
            marker: [ll.lat, ll.lng],
          });
          return;
        }

        // Polyline tool: a road is a line, not an area — the parallel of the
        // polygon branch below, minus area (a line has none) and using the
        // flat-array extractor (see linePositions' comment).
        if (layerType === 'polyline' && layer) {
          const fg = internalFGRef.current as unknown as InnerFG | null;
          if (fg?._layers) {
            Object.values(fg._layers).forEach((existing) => {
              // Polygon extends Polyline in Leaflet's class hierarchy, so
              // this must explicitly exclude polygons or it would delete
              // them too on a mixed-tool FeatureGroup.
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const Lany = L as any;
              if (
                existing !== layer &&
                Lany.Polyline &&
                existing instanceof Lany.Polyline &&
                !(Lany.Polygon && existing instanceof Lany.Polygon)
              ) {
                fg.removeLayer(existing);
              }
            });
          }
          onCreated?.({
            raw: e,
            positions: linePositions(layer),
            shapeType: 'polyline',
            areaHectares: null,
            geometry: layerToGeoJSON(layer),
          });
          return;
        }

        if (layerType !== 'polygon' || !layer) {
          if (onCreated) {
            onCreated({
              raw: e,
              positions: null,
              areaHectares: null,
              geometry: null,
              marker: null,
            });
          }
          return;
        }

        // Replace any previously seeded editable polygon with this new one
        // so the FeatureGroup contains exactly one drawable polygon.
        const fg = internalFGRef.current as unknown as InnerFG | null;
        if (fg?._layers) {
          Object.values(fg._layers).forEach((existing) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if (existing !== layer && (L as any).Polygon && existing instanceof (L as any).Polygon) {
              fg.removeLayer(existing);
            }
          });
        }

        const positions = firstRingPositions(layer);
        const area = polygonToHectares(layer.getLatLngs?.()?.[0]);
        onCreated?.({
          raw: e,
          positions,
          shapeType: 'polygon',
          areaHectares: area,
          geometry: layerToGeoJSON(layer),
        });

        // Same vertex-drag relay as initialGeometry — leaflet-draw's own
        // `draw:edited` only fires on toolbar Save, which users miss.
        if (onEdited) {
          layer.on('edit', () => {
            const nextPositions = firstRingPositions(layer);
            const nextArea = polygonToHectares(layer.getLatLngs?.()?.[0]);
            onEdited({
              raw: { type: 'edit' } as L.LeafletEvent,
              positions: nextPositions,
              areaHectares: nextArea,
              geometry: layerToGeoJSON(layer),
            });
          });
        }
      },
      [onCreated, onEdited],
    );

    const handleEdited = useCallback(
      (e: L.LeafletEvent) => {
        if (!onEdited) return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const layers = (e as any).layers;
        if (!layers || typeof layers.eachLayer !== 'function') return;
        layers.eachLayer((layer: L.Layer) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if (!(L as any).Polygon || !(layer instanceof (L as any).Polygon)) return;
          const positions = firstRingPositions(layer);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const area = polygonToHectares((layer as any).getLatLngs?.()?.[0]);
          onEdited({
            raw: e,
            positions,
            areaHectares: area,
            geometry: layerToGeoJSON(layer),
          });
        });
      },
      [onEdited],
    );

    const handleDeleted = useCallback(
      (e: L.LeafletEvent) => {
        const fg = internalFGRef.current as unknown as InnerFG | null;
        let anyLeft = false;
        if (fg?._layers) {
          Object.values(fg._layers).forEach((layer) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if ((L as any).Polygon && layer instanceof (L as any).Polygon) anyLeft = true;
          });
        }
        onDeleted?.({ raw: e, allCleared: !anyLeft });
      },
      [onDeleted],
    );

    // Memoize the draw/edit props identity so EditControl doesn't re-mount.
    const stableDraw = useMemo(() => drawOptions, [drawOptions]);
    const stableEdit = useMemo(() => editOptions, [editOptions]);

    return (
      <FeatureGroup
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ref={(fg: any) => setFGRef(fg as L.FeatureGroup | null)}
      >
        <EditControl
          position={position}
          onCreated={handleCreated}
          onEdited={handleEdited}
          onDeleted={handleDeleted}
          draw={stableDraw}
          edit={stableEdit}
        />
      </FeatureGroup>
    );
  },
);

export default MapDrawControl;
